import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Shield, LogOut } from "lucide-react";
import { http } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const TIERS = [
  { key: "essentiel", name: "Essentiel", price: "19,99 € HT/mois" },
  { key: "pro",       name: "Pro",       price: "34,99 € HT/mois" },
  { key: "integral",  name: "Intégral",  price: "49,99 € HT/mois" },
];

/** Private page — only reachable by super_admin. All state is server-authoritative;
 *  the /api/admin/simulate routes 403 non-super-admins even if they URL-hack here. */
export default function SuperSimulator() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await http.get("/plan/me");
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger l'état.");
    }
  };

  useEffect(() => { if (user) refresh(); /* eslint-disable-next-line */ }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/connexion" replace />;
  // Client-side gate — the server still enforces 403 independently.
  if (state && !state.is_super_admin) return <Navigate to="/aujourdhui" replace />;

  const setPlan = async (plan) => {
    setBusy(true);
    try {
      await http.post("/admin/simulate", { plan });
      await refresh();
      toast.success(`Mode simulation : ${TIERS.find((t) => t.key === plan)?.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur simulation.");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await http.post("/admin/simulate/reset");
      await refresh();
      toast.success("Simulation quittée.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur reset.");
    } finally { setBusy(false); }
  };

  const active = state?.simulated ? state.effective_plan : null;

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="super-simulator-page">
      <div className="flex items-center gap-2 text-[#0B1F33]">
        <Shield className="w-5 h-5 text-[#FF9F1C]" />
        <h1 className="font-heading text-2xl font-extrabold">Simulateur de formule</h1>
      </div>
      <p className="mt-2 text-[13px] text-[#5E6875]">
        Bascule instantanément entre Essentiel, Pro et Intégral pour tester les blocages en conditions
        réelles. Ce mode ne modifie jamais votre abonnement Stripe.
      </p>

      <div className="mt-6 rounded-lg border border-[#CDD7E3] bg-white p-4">
        <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">État actuel</div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[13px]">
          <span>Formule effective : <b className="text-[#0B1F33]" data-testid="sim-effective">{state?.effective_plan || "…"}</b></span>
          {state?.simulated && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FFF0D2] text-[#7A4C0B] text-[11.5px] font-bold border border-[#FFD166]" data-testid="sim-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF9F1C] animate-pulse" /> Mode simulation
            </span>
          )}
          <span className="text-[#5E6875]">Abonnement réel : <b className="text-[#0B1F33]">{state?.subscription_plan || "essai"}</b></span>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-3" role="tablist" aria-label="Choix de formule">
        {TIERS.map((t) => {
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={busy}
              data-testid={`sim-tier-${t.key}`}
              onClick={() => setPlan(t.key)}
              className={`text-left rounded-md border p-4 transition-all min-h-[110px] ${
                isActive
                  ? "border-[#FF9F1C] bg-[#FFF0D2]/60 ring-2 ring-[#FF9F1C]/30"
                  : "border-[#CDD7E3] bg-white hover:border-[#1769E0]"
              }`}
            >
              <div className="text-[11px] uppercase tracking-widest text-[#5E6875] font-semibold">{t.name}</div>
              <div className="mt-1 text-[16px] font-heading font-extrabold text-[#0B1F33]">{t.price}</div>
              <div className="mt-2 text-[11.5px] text-[#5E6875]">{isActive ? "Simulation active" : "Basculer sur cette formule"}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={reset}
          disabled={busy || !state?.simulated}
          data-testid="sim-reset-btn"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#CDD7E3] text-[13px] font-semibold disabled:opacity-50 hover:border-[#0B1F33]"
        >
          <LogOut className="w-4 h-4" /> Quitter la simulation
        </button>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          data-testid="sim-refresh-btn"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#CDD7E3] text-[13px] font-semibold disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          Rafraîchir
        </button>
      </div>

      {state?.limits && (
        <div className="mt-6 rounded-lg border border-[#CDD7E3] bg-[#F4F5EF] p-4 text-[12.5px]" data-testid="sim-limits">
          <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-2">Limites appliquées</div>
          <ul className="space-y-1 font-mono-num tabular-nums text-[#0B1F33]">
            <li>Factures / mois : <b>{fmt(state.limits.invoices_per_month)}</b></li>
            <li>Produits (max) : <b>{fmt(state.limits.products_max)}</b></li>
            <li>Recettes (max) : <b>{fmt(state.limits.recipes_max)}</b></li>
            <li>Historique : <b>{fmt(state.limits.history_days)} j</b></li>
            <li>Négociation : <b>{state.limits.negotiation}</b></li>
            <li>Langues menu : <b>{state.limits.translations_allowed}</b></li>
          </ul>
        </div>
      )}
    </div>
  );
}

function fmt(v) { return v >= 1e9 ? "illimité" : String(v); }
